import { useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Card, Chip } from "@vb/ui";
import { Seo } from "../lib/seo.js";
import { Section, SectionHeading } from "../components/Section.js";
import { VaultDoor } from "../components/cinematic/VaultDoor.js";
import { liveVaultState } from "../components/cinematic/liveVaultState.js";
import { GAMES, type GameCategory, type GameMeta } from "../content/games.js";

const FILTERS = ["All", "Crash", "Mines", "Puzzle", "Dice", "Table"] as const;
type Filter = (typeof FILTERS)[number];

function matchesFilter(game: GameMeta, filter: Filter): boolean {
  if (filter === "All") return true;
  return game.category === (filter.toLowerCase() as GameCategory);
}

export function Games() {
  const [active, setActive] = useState<Filter>("All");
  const live = GAMES.filter((g) => g.status === "live" && matchesFilter(g, active));
  const inDev = GAMES.filter((g) => g.status === "in-dev" && matchesFilter(g, active));

  return (
    <>
      <Seo title="Games · Vault Breaker Studios" description="Our library of crypto casino originals." path="/games" />

      <Section style={{ paddingBottom: 24 }}>
        <SectionHeading
          eyebrow="Catalogue"
          title="Games"
          lead="Three live titles and three in development. All built against the same engine and RGS surface — adding a new one is a package swap, not a new integration."
        />
        <div style={{ display: "flex", gap: 8, marginBottom: 24, flexWrap: "wrap" }}>
          {FILTERS.map((f) => {
            const isActive = f === active;
            return (
              <Chip
                key={f}
                role="button"
                tabIndex={0}
                onClick={() => setActive(f)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setActive(f);
                  }
                }}
                style={{
                  cursor: "pointer",
                  background: isActive ? "var(--bg-4)" : "var(--bg-3)",
                  borderColor: isActive ? "var(--line-strong)" : "var(--line)",
                  color: isActive ? "var(--fg-1)" : "var(--fg-3)",
                }}
              >
                {f}
              </Chip>
            );
          })}
        </div>
      </Section>

      <Section style={{ paddingTop: 0 }}>
        {live.length + inDev.length === 0 ? (
          <p style={{ fontSize: 14, color: "var(--fg-3)", margin: 0 }}>
            No games in this category yet.
          </p>
        ) : (
          <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
            {live.map((g) => (
              <LiveTile key={g.slug} g={g} />
            ))}
            {inDev.map((g) => (
              <InDevTile key={g.slug} g={g} />
            ))}
          </div>
        )}
      </Section>
    </>
  );
}

function LiveTile({ g }: { g: GameMeta }) {
  return (
    <Link
      to={`/games/${g.slug}`}
      style={{ textDecoration: "none", color: "inherit" }}
    >
      <Card
        className="vb-tile"
        style={{
          padding: 0,
          overflow: "hidden",
          cursor: "pointer",
          display: "grid",
          gridTemplateRows: "180px auto",
        }}
      >
        <div
          style={{
            background:
              "radial-gradient(ellipse at 50% 30%, rgba(212,162,76,0.18), transparent 70%), linear-gradient(180deg, var(--bg-3), var(--bg-2))",
            display: "grid",
            placeItems: "center",
            position: "relative",
          }}
        >
          <GameThumb slug={g.slug} />
          <Badge kind="live" style={{ position: "absolute", top: 12, left: 12 }}>
            LIVE
          </Badge>
        </div>
        <div style={{ padding: 16, display: "grid", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
            <h3 style={{ margin: 0, fontSize: 18 }}>{g.name}</h3>
            <span className="mono" style={{ fontSize: 11, color: "var(--fg-3)" }}>
              {g.roundSeconds > 0 ? `${g.roundSeconds}s · ` : ""}{g.rtpPercent}% RTP
            </span>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: "var(--fg-2)" }}>{g.tagline}</p>
        </div>
      </Card>
    </Link>
  );
}

/** Per-slug thumbnail. Vault Breaker keeps the existing live VaultDoor;
 *  Pressure Plate gets its own 5×5 mini-grid SVG so the catalog doesn't
 *  show two identical vault doors next to each other. */
export function GameThumb({ slug }: { slug: string }) {
  if (slug === "pressure-plate") return <PressurePlateThumb />;
  if (slug === "cipher-crack") return <CipherCrackThumb />;
  return (
    <div style={{ width: 180, height: 180, position: "relative" }}>
      <VaultDoor state={liveVaultState(0, "betting", 2, 1)} size={180} />
    </div>
  );
}

/** Static catalog preview of Cipher Crack — four combination tumblers
 *  showing the digits 7-4-9-2 with a hairline gold rim. Brand-aligned. */
export function CipherCrackThumb() {
  const code = [7, 4, 9, 2];
  return (
    <div
      style={{
        width: 180,
        height: 180,
        position: "relative",
        display: "grid",
        placeItems: "center",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse 70% 90% at 50% 0%, rgba(241,203,107,0.18) 0%, transparent 60%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          padding: 10,
          background: "#0A0D14",
          border: "1px solid rgba(212,162,76,0.42)",
          borderRadius: 4,
          boxShadow: "0 10px 30px rgba(0,0,0,0.6)",
        }}
      >
        {code.map((d, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div
              style={{
                width: 28,
                height: 38,
                background: "linear-gradient(180deg, #181D29 0%, #0B0E16 100%)",
                border: "1px solid rgba(196,154,70,0.42)",
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                fontFamily: "var(--font-display)",
                fontSize: 22,
                color: "var(--gold-bright)",
                letterSpacing: -1,
                textShadow: "0 0 6px var(--gold)",
              }}
            >
              {d}
            </div>
            {i < 3 && (
              <div
                style={{
                  width: 4,
                  height: 6,
                  background: "var(--gold-deep)",
                  borderRadius: 1,
                  opacity: 0.7,
                }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/** A static preview of the Pressure Plate floor — mid-round, two safe tiles
 *  cleared (gold glyph) and one mine triggered (red bell). Vault-floor look:
 *  matte tiles with hairline gold outlines, dim crosshatch overlay, single
 *  cone of overhead light. Brand-aligned with the real game stage. */
export function PressurePlateThumb() {
  // 5x5 grid coordinates encoded as which cells are revealed
  const safes = new Set([6, 12, 18, 22]); // flat indices that show gold glyph
  const mine = 14; // single revealed mine
  const tiles: Array<{ idx: number; state: "hidden" | "safe" | "mine" }> = [];
  for (let i = 0; i < 25; i += 1) {
    const state = i === mine ? "mine" : safes.has(i) ? "safe" : "hidden";
    tiles.push({ idx: i, state });
  }
  const tileSize = 26;
  const gap = 3;
  const padding = 8;
  const grid = 5 * tileSize + 4 * gap + 2 * padding; // 158px

  return (
    <div
      style={{
        width: 180,
        height: 180,
        position: "relative",
        display: "grid",
        placeItems: "center",
      }}
    >
      {/* Overhead light cone */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse 70% 90% at 50% 0%, rgba(241,203,107,0.18) 0%, transparent 60%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          width: grid,
          height: grid,
          padding,
          background: "#0A0D14",
          border: "1px solid rgba(212,162,76,0.42)",
          borderRadius: 4,
          boxShadow:
            "0 12px 40px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.03)",
          display: "grid",
          gridTemplateColumns: `repeat(5, ${tileSize}px)`,
          gridTemplateRows: `repeat(5, ${tileSize}px)`,
          gap,
          position: "relative",
        }}
      >
        {tiles.map((t) => {
          if (t.state === "safe") {
            return (
              <div
                key={t.idx}
                style={{
                  background:
                    "linear-gradient(180deg, rgba(196,154,70,0.30) 0%, rgba(140,106,42,0.15) 100%), linear-gradient(180deg, #161B27 0%, #0F1320 100%)",
                  border: "1px solid rgba(212,162,76,0.42)",
                  borderRadius: 2,
                  display: "grid",
                  placeItems: "center",
                  boxShadow:
                    "inset 0 0 12px rgba(241,203,107,0.35), 0 0 8px rgba(241,203,107,0.25)",
                }}
              >
                <svg width="13" height="13" viewBox="-12 -12 24 24" fill="none" stroke="var(--gold-bright)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M0 -8 L7 -5 L7 3 Q7 7 0 9 Q-7 7 -7 3 L-7 -5 Z" />
                  <circle cx="0" cy="-1" r="1.2" fill="var(--gold-bright)" />
                </svg>
              </div>
            );
          }
          if (t.state === "mine") {
            return (
              <div
                key={t.idx}
                style={{
                  background:
                    "linear-gradient(180deg, rgba(224,69,69,0.45) 0%, rgba(106,26,26,0.35) 100%), linear-gradient(180deg, #161B27 0%, #0F1320 100%)",
                  border: "1px solid rgba(255,106,106,0.7)",
                  borderRadius: 2,
                  display: "grid",
                  placeItems: "center",
                  boxShadow:
                    "inset 0 0 12px rgba(224,69,69,0.5), 0 0 10px rgba(224,69,69,0.4)",
                }}
              >
                <svg width="13" height="13" viewBox="-12 -12 24 24" fill="none" stroke="var(--red-bright)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M-6 4 Q-6 -6 0 -8 Q6 -6 6 4 L7.5 5.5 L-7.5 5.5 Z" fill="var(--red-bright)" fillOpacity="0.18" />
                  <line x1="-2" y1="7.5" x2="2" y2="7.5" />
                </svg>
              </div>
            );
          }
          return (
            <div
              key={t.idx}
              style={{
                background: "linear-gradient(180deg, #161B27 0%, #0F1320 100%)",
                border: "1px solid rgba(212,162,76,0.18)",
                borderRadius: 2,
              }}
            />
          );
        })}
        {/* Crosshatch */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(0deg, rgba(212,162,76,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(212,162,76,0.18) 1px, transparent 1px)",
            backgroundSize: "20px 20px",
            opacity: 0.3,
            mixBlendMode: "overlay",
            pointerEvents: "none",
          }}
        />
      </div>
    </div>
  );
}

function InDevTile({ g }: { g: GameMeta }) {
  return (
    <Card
      style={{
        padding: 0,
        overflow: "hidden",
        display: "grid",
        gridTemplateRows: "180px auto",
        opacity: 0.9,
      }}
    >
      <div
        style={{
          background: "linear-gradient(135deg, var(--bg-3), var(--bg-1))",
          display: "grid",
          placeItems: "center",
          position: "relative",
          fontFamily: "var(--font-display)",
          fontSize: 40,
          color: "var(--fg-3)",
          letterSpacing: "0.06em",
        }}
      >
        {g.name.split(" ").map((w) => w[0]).join("")}
        <span
          style={{
            position: "absolute",
            top: 12,
            left: 12,
            fontFamily: "var(--font-ui)",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.12em",
            padding: "4px 10px",
            borderRadius: 999,
            background: "rgba(0,229,255,0.12)",
            color: "var(--cyan)",
            border: "1px solid rgba(0,229,255,0.35)",
          }}
        >
          IN DEV
        </span>
      </div>
      <div style={{ padding: 16, display: "grid", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 18 }}>{g.name}</h3>
          <span className="mono" style={{ fontSize: 11, color: "var(--fg-3)" }}>
            {g.roundSeconds}s · {g.rtpPercent}% RTP
          </span>
        </div>
        <p style={{ margin: 0, fontSize: 13, color: "var(--fg-2)" }}>{g.tagline}</p>
      </div>
    </Card>
  );
}
